import { api } from 'api'
import { path } from 'ramda'
import { Position, PositionTemplate, Roster } from 'typings/common_defs'
import {
  MarkdownContent,
  QuizQuestion,
  Tutorial,
  TutorialCategory,
} from 'api/shift/typings'
import { toSnakecaseKeys } from 'lib/string'

export const AllPositionTutorialCategories = [
  TutorialCategory.General,
  TutorialCategory.AttireAndGrooming,
]

export const sortTutorialsByCategory = (tutorials: Tutorial[]) => {
  return tutorials.sort((a: Tutorial, b: Tutorial) => {
    const aCategory = a?.category as TutorialCategory
    const bCategory = b?.category as TutorialCategory
    const aIndex = AllPositionTutorialCategories.indexOf(aCategory)
    const bIndex = AllPositionTutorialCategories.indexOf(bCategory)

    // If both categories are not in the predefined list (index = -1)
    // maintain their original order
    if (aIndex === -1 && bIndex === -1) return 0

    // If one category is not in the list, it should go after the listed categories
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1

    // If both categories are in the list, sort by their index
    return aIndex - bIndex
  }) as Tutorial[]
}

export async function getCompanyPositions() {
  const data = await api.post(
    '/company/graphql_dispatch/current_company_04bfc888',
    {}
  )

  return path(['data', 'data', 'company', 'positions'], data) as Position[]
}

type GetPositionsRequest = {
  companyId: number
  positionType?: 'standard' | 'lead'
}

type GetPositionsResponsePosition = {
  id: number
  name: string
  positionType: 'standard' | 'lead'
  hidden: boolean
}

type GetPositionsResponse = {
  positions: GetPositionsResponsePosition[]
}

export async function getPositions(
  { companyId, positionType }: GetPositionsRequest,
  signal?: AbortSignal
) {
  const response = await api.get<GetPositionsResponse>(
    `/company/${companyId}/position`,
    { params: { positionType }, signal }
  )

  return response.data.positions
}

export interface SimplePositionTemplateGroup {
  id: number
  name: string
  positionTemplates: Array<{ id: number; name: string }>
}

export async function getPositionTemplateGroups() {
  const data = await api.post(
    '/company/graphql_dispatch/position_template_groups_867f2b9c',
    {}
  )

  const groups = path(
    ['data', 'data', 'company', 'positionTemplateGroups'],
    data
  ) as SimplePositionTemplateGroup[]

  return groups.map((group) => ({
    ...group,
    id: Number(group.id),
    positionTemplates: group.positionTemplates.map((template) => ({
      ...template,
      id: Number(template.id),
    })),
  }))
}

export async function getPositionTemplate(positionTemplateId: number) {
  const data = await api.post(
    '/company/graphql_dispatch/position_templates_9ec96ade',
    {
      positionTemplateId,
    }
  )

  const template = path(
    ['data', 'data', 'company', 'positionTemplates', 0],
    data
  ) as PositionTemplate

  return template
    ? {
        ...template,
        id: Number(template.id),
      }
    : undefined
}

export async function getPositionRoster(positionId: number) {
  const data = await api.post(
    '/company/graphql_dispatch/position_roster_cc1c2964',
    {
      positionId,
    }
  )

  const roster = path(
    ['data', 'data', 'company', 'positions', 0, 'roster'],
    data
  ) as Roster
  return roster
}

export async function getPositionTutorials(positionId: number) {
  const data = await api.post(
    '/company/graphql_dispatch/position_tutorials_fd510ac5',
    {
      positionId,
    }
  )

  const tutorials = path(
    ['data', 'data', 'company', 'positions', 0, 'tutorials'],
    data
  ).map(
    (
      tutorial: Omit<Tutorial, 'content' | 'quiz'> & {
        content?: string
        quiz?: string
      }
    ) => ({
      ...tutorial,
      content: tutorial.content ? JSON.parse(tutorial.content) : [],
      quiz: tutorial.quiz ? JSON.parse(tutorial.quiz) : tutorial.quiz,
    })
  )

  return sortTutorialsByCategory(tutorials)
}

export async function getPositionTutorial(
  positionId: number,
  tutorialId: number
) {
  const tutorials = await getPositionTutorials(positionId)
  return (tutorials || []).find(
    (tutorial) => tutorial.id.toString() === tutorialId.toString()
  )
}

export interface PositionRequest {
  id?: number
  name?: string | null
  about?: string | null
  positionTemplateId?: number
  workerRequirementIds?: Array<number>
  customRequirements?: Array<{ name?: string; category?: string }>
  isShiftLeadPosition?: boolean
}

export interface CreatePositionRequest extends PositionRequest {
  positionTemplateId: number
}

export async function createCompanyPosition(
  companyId: number,
  data: CreatePositionRequest
) {
  const response = await api.post(
    `/company/${companyId}/position`,
    toSnakecaseKeys(data)
  )
  return path(['data', 'id'], response)
}

export async function updateCompanyPosition(
  companyId: number,
  positionId: number,
  data: PositionRequest
) {
  await api.put(
    `/company/${companyId}/position/${positionId}`,
    toSnakecaseKeys(data)
  )
}

export interface CreatePositionTutorialRequest {
  companyId: number
  positionId: number
  category: TutorialCategory
  content: MarkdownContent[]
  quiz?: QuizQuestion[]
  notifyWorkers?: boolean
}

export async function createPositionTutorial(
  request: CreatePositionTutorialRequest
): Promise<number> {
  const { companyId, positionId, category, content, quiz, notifyWorkers } =
    request
  const data = await api.post(
    `/company/${companyId}/position/${positionId}/tutorial`,
    {
      category,
      content,
      quiz,
      notify_workers: notifyWorkers,
    }
  )

  const tutorialId = path(['data', 'tutorial_id'], data)
  return tutorialId
}

export type EditPositionTutorialRequest = Omit<
  CreatePositionTutorialRequest,
  'category'
> & {
  tutorialId: number
}

export async function editPositionTutorial(
  request: EditPositionTutorialRequest
): Promise<number> {
  const { companyId, positionId, tutorialId, content, quiz, notifyWorkers } =
    request
  const data = await api.put(
    `/company/${companyId}/position/${positionId}/tutorial/${tutorialId}`,
    {
      content,
      quiz,
      notify_workers: notifyWorkers,
    }
  )
  return path(['data', 'id'], data)
}

export interface RemovePositionTutorialRequest {
  companyId: number
  positionId: number
  tutorialId: number
}

export async function removePositionTutorial(
  request: RemovePositionTutorialRequest
) {
  const { companyId, positionId, tutorialId } = request
  await api.delete(
    `/company/${companyId}/position/${positionId}/tutorial/${tutorialId}`
  )
}
